// Vollea — League Management modal (edit divisions, archive, delete)
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoClose, IoAdd, IoTrashOutline, IoArchive, IoArrowUndo, IoWarning, IoCheckmark } from "react-icons/io5";
import { Btn } from "./ui-vollea";
import { api } from "../lib/api";
import { C, SP, R } from "../lib/theme";

export default function LeagueManageModal({ league, onClose, onChanged }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("divisions");
  const [divs, setDivs] = useState(
    (league.divisions || []).map((d) => ({ old: d, name: d }))
  );
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgOk, setMsgOk] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState("");

  const reportOk = (t) => { setMsgOk(true); setMsg(t); };
  const reportErr = (e) => { setMsgOk(false); setMsg(e.message || "Something went wrong"); };

  const setName = (i, v) => setDivs((arr) => arr.map((d, idx) => idx === i ? { ...d, name: v } : d));
  const removeDiv = (i) => setDivs((arr) => arr.filter((_, idx) => idx !== i));
  const addDiv = () => setDivs((arr) => [...arr, { old: null, name: "" }]);

  const saveDivisions = async () => {
    setBusy(true); setMsg("");
    try {
      const cleaned = divs.map((d) => ({ old: d.old, name: d.name.trim() })).filter((d) => d.name);
      if (cleaned.length === 0) throw new Error("A league must have at least one division");
      const updated = await api.put(`/leagues/${league.league_id}/divisions`, { divisions: cleaned });
      reportOk("Divisions saved.");
      setDivs((updated.divisions || []).map((d) => ({ old: d, name: d })));
      onChanged?.();
    } catch (e) { reportErr(e); }
    finally { setBusy(false); }
  };

  const toggleArchive = async () => {
    setBusy(true); setMsg("");
    try {
      const path = league.archived ? "unarchive" : "archive";
      await api.post(`/leagues/${league.league_id}/${path}`);
      reportOk(league.archived ? "League restored." : "League archived.");
      onChanged?.();
      setTimeout(onClose, 700);
    } catch (e) { reportErr(e); }
    finally { setBusy(false); }
  };

  const deleteLeague = async () => {
    setBusy(true); setMsg("");
    try {
      await api.del(`/leagues/${league.league_id}`);
      onChanged?.({ deleted: true });
      onClose();
      navigate("/leagues");
    } catch (e) { reportErr(e); setBusy(false); }
  };

  const input = { width: "100%", background: C.surface2, borderRadius: R.md, border: `1px solid ${C.border}`, color: C.text, padding: `0 ${SP.lg}px`, height: 48, fontSize: 15, outline: "none" };

  return (
    <div
      role="dialog"
      data-testid="league-manage-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480, background: C.surface, borderTop: `1px solid ${C.border}`,
          borderRadius: `${R.lg}px ${R.lg}px 0 0`, padding: `${SP.lg}px ${SP.lg}px 40px`,
          maxHeight: "92vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SP.md }}>
          <h2 style={{ color: C.text, fontWeight: 700, fontSize: 22, margin: 0, letterSpacing: 0.4 }}>Manage league</h2>
          <button type="button" onClick={onClose} data-testid="league-manage-close" style={{ color: C.textMuted, padding: 4 }}>
            <IoClose size={26} />
          </button>
        </div>

        <div style={{ display: "flex", gap: SP.sm, marginBottom: SP.lg, overflowX: "auto" }}>
          <TabPill label="Divisions" active={tab === "divisions"} onClick={() => setTab("divisions")} testid="league-manage-tab-divisions" />
          <TabPill label={league.archived ? "Restore" : "Archive"} active={tab === "archive"} onClick={() => setTab("archive")} testid="league-manage-tab-archive" />
          <TabPill label="Delete" active={tab === "delete"} onClick={() => setTab("delete")} danger testid="league-manage-tab-delete" />
        </div>

        {msg ? (
          <div data-testid="league-manage-msg" style={{ marginBottom: SP.md, background: msgOk ? C.limeTint : "rgba(239,68,68,0.1)", border: `1px solid ${msgOk ? C.lime : C.error}`, color: msgOk ? C.lime : C.error, padding: SP.md, borderRadius: R.md, fontSize: 13, fontWeight: 600 }}>{msg}</div>
        ) : null}

        {tab === "divisions" && (
          <>
            <p style={{ color: C.textMuted, fontSize: 13, margin: `0 0 ${SP.md}px` }}>
              Add up to 6 divisions, rename existing ones, or remove empty ones. Removing a division with players or fixtures will fail — move them first.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: SP.sm }}>
              {divs.map((d, i) => (
                <div key={i} data-testid={`division-row-${i}`} style={{ display: "flex", alignItems: "center", gap: SP.sm }}>
                  <input
                    value={d.name}
                    onChange={(e) => setName(i, e.target.value)}
                    placeholder="Division name (e.g. A)"
                    style={input}
                    data-testid={`division-name-${i}`}
                    maxLength={20}
                  />
                  <button type="button" onClick={() => removeDiv(i)} data-testid={`division-remove-${i}`} disabled={busy} style={{ padding: 10, borderRadius: R.sm, background: C.surface3, color: C.error }}>
                    <IoTrashOutline size={16} />
                  </button>
                </div>
              ))}
              {divs.length < 6 && (
                <button type="button" onClick={addDiv} data-testid="division-add" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, height: 44, borderRadius: R.md, background: C.surface2, color: C.text, border: `1px dashed ${C.borderStrong}`, fontWeight: 600, fontSize: 13 }}>
                  <IoAdd size={16} /> Add division
                </button>
              )}
            </div>
            <div style={{ height: SP.lg }} />
            <Btn label="Save divisions" loading={busy} onClick={saveDivisions} testid="divisions-save-button" icon={<IoCheckmark size={16} />} />
          </>
        )}

        {tab === "archive" && (
          <>
            <p style={{ color: C.textMuted, fontSize: 13, margin: `0 0 ${SP.md}px` }}>
              {league.archived
                ? "Restore this league so players can join and play again."
                : "Archive this league to lock it from new joins. Existing data is preserved and you can restore it anytime."}
            </p>
            <Btn
              label={league.archived ? "Restore league" : "Archive league"}
              icon={league.archived ? <IoArrowUndo size={16} /> : <IoArchive size={16} />}
              loading={busy}
              onClick={toggleArchive}
              testid="league-archive-button"
              variant={league.archived ? "primary" : "dark"}
            />
          </>
        )}

        {tab === "delete" && (
          <>
            <div style={{ background: "rgba(239,68,68,0.08)", border: `1px solid ${C.error}`, padding: SP.md, borderRadius: R.md, marginBottom: SP.md }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.error, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>
                <IoWarning size={14} /> Permanent action
              </div>
              <p style={{ color: C.text, fontSize: 13, margin: `${SP.sm}px 0 0`, lineHeight: 1.5 }}>
                This cascades through every membership, team, fixture and partner-invite tied to <b>{league.name}</b>. There is no undo.
              </p>
            </div>
            <label style={{ color: C.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6, display: "block" }}>
              Type the league name to confirm
            </label>
            <input
              data-testid="league-delete-confirm-input"
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
              placeholder={league.name}
              style={input}
            />
            <div style={{ height: SP.lg }} />
            <button
              data-testid="league-delete-button"
              disabled={busy || confirmDelete.trim() !== league.name.trim()}
              onClick={deleteLeague}
              style={{ width: "100%", height: 54, borderRadius: R.md, background: C.error, color: "#fff", fontWeight: 700, fontSize: 14, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: (busy || confirmDelete.trim() !== league.name.trim()) ? 0.4 : 1 }}
            >
              <IoTrashOutline size={16} /> Delete league forever
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function TabPill({ label, active, onClick, danger, testid }) {
  const activeBg = danger ? C.error : C.lime;
  const activeFg = danger ? "#fff" : C.onLime;
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        height: 36, padding: `0 ${SP.lg}px`, borderRadius: R.pill,
        background: active ? activeBg : C.surface2,
        border: `1px solid ${active ? activeBg : C.border}`,
        color: active ? activeFg : (danger ? C.error : C.textMuted),
        fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase",
        flexShrink: 0,
      }}
    >
      {label}
    </button>
  );
}
